import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { salespersonsAPI, formatKSH } from "../services/api";
import SalespersonModal from "../components/SalespersonModal";
import DataTable from "../components/DataTable";

const Salespersons = () => {
  const { isAdmin } = useAuth();
  const [salespersons, setSalespersons] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState("");
  const [showModal, setShowModal]       = useState(false);
  const [editingSalesperson, setEditingSalesperson] = useState(null);

  /* ── data ────────────────────────────────────────────────────────── */
  const fetchSalespersons = useCallback(async () => {
    try {
      setLoading(true);
      const response = await salespersonsAPI.getAll();
      setSalespersons(response.data);
    } catch (err) {
      setError("Failed to load salespersons");
      console.error("Salespersons error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSalespersons(); }, [fetchSalespersons]);

  /* ── handlers ────────────────────────────────────────────────────── */
  const handleCreate = ()             => { setEditingSalesperson(null); setShowModal(true); };
  const handleEdit   = salesperson    => { setEditingSalesperson(salesperson); setShowModal(true); };
  const handleClose  = ()             => { setShowModal(false); setEditingSalesperson(null); fetchSalespersons(); };

  const handleDelete = async (id) => {
    if (!window.confirm("Deactivate this salesperson?")) return;
    try {
      await salespersonsAPI.delete(id);
      fetchSalespersons();
    } catch (err) {
      alert("Failed to deactivate: " + (err.response?.data?.message || err.message));
    }
  };

  /* ── stats ───────────────────────────────────────────────────────── */
  const totalInteractions = salespersons.reduce((s, sp) => s + parseInt(sp.interaction_count || 0), 0);
  const totalOrders       = salespersons.reduce((s, sp) => s + parseInt(sp.order_count || 0), 0);
  const totalSales        = salespersons.reduce((s, sp) => s + parseFloat(sp.total_sales || 0), 0);

  /* ── columns ─────────────────────────────────────────────────────── */
  const columns = [
    {
      key: '_no', label: '#',
      sortable: false, export: false,
      thClass: 'w-12',
      render: (_, __, idx) => (
        <span className="text-gray-400 font-mono text-xs">{idx + 1}</span>
      ),
    },
    {
      key: 'name', label: 'Name',
      exportValue: r => r.name,
      render: (val, row) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-xs font-semibold text-white">
              {val?.charAt(0)?.toUpperCase()}
            </span>
          </div>
          <Link
            to={`/salespersons/${row.id}`}
            className="font-medium text-primary-600 hover:text-primary-800 hover:underline"
          >{val}</Link>
        </div>
      ),
    },
    {
      key: 'employee_id', label: 'Employee ID',
      exportValue: r => r.employee_id || '',
    },
    {
      key: 'email', label: 'Email',
      exportValue: r => r.email || '',
      render: val => val || <span className="text-gray-400 text-xs">—</span>,
    },
    {
      key: 'phone', label: 'Phone',
      exportValue: r => r.phone || '',
      render: val => val || <span className="text-gray-400 text-xs">—</span>,
    },
    {
      key: 'interaction_count', label: 'Interactions',
      exportValue: r => r.interaction_count ?? 0,
      render: val => (
        <span className="inline-flex items-center justify-center w-8 h-6 rounded bg-primary-50 text-primary-700 text-xs font-semibold">
          {val ?? 0}
        </span>
      ),
    },
    {
      key: 'order_count', label: 'Orders',
      exportValue: r => r.order_count ?? 0,
      render: val => (
        <span className="inline-flex items-center justify-center w-8 h-6 rounded bg-success-50 text-success-700 text-xs font-semibold">
          {val ?? 0}
        </span>
      ),
    },
    {
      key: 'total_sales', label: 'Total Sales (KSH)',
      exportValue: r => parseFloat(r.total_sales || 0).toFixed(2),
      render: val => (
        <span className="font-semibold text-purple-600">
          {formatKSH(val || 0)}
        </span>
      ),
    },
    {
      key: 'actions', label: 'Actions',
      sortable: false, export: false,
      thClass: 'text-right',
      tdClass: 'text-right',
      render: (_, row) => (
        <div className="flex items-center justify-end gap-1">
          <Link
            to={`/salespersons/${row.id}`}
            className="btn btn-ghost p-1.5 text-gray-600 hover:text-primary-600"
            title="View Details"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </Link>
          {isAdmin && (
            <>
              <button
                onClick={() => handleEdit(row)}
                className="btn btn-ghost p-1.5 text-gray-600 hover:text-primary-600"
                title="Edit"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
              <button
                onClick={() => handleDelete(row.id)}
                className="btn btn-ghost p-1.5 text-red-500 hover:text-red-700"
                title="Deactivate"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  const searchFields = ['name', 'employee_id', 'email', 'phone'];

  /* ── render ──────────────────────────────────────────────────────── */
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Salesperson Management</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage your sales team and track their performance
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={handleCreate}
            className="btn btn-primary inline-flex items-center gap-2 self-start sm:self-auto"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add Salesperson
          </button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Salespersons', value: salespersons.length,   color: 'text-primary-600',  bg: 'bg-primary-50' },
          { label: 'Total Interactions', value: totalInteractions,     color: 'text-success-600',  bg: 'bg-success-50' },
          { label: 'Total Orders',       value: totalOrders,           color: 'text-warning-600',  bg: 'bg-warning-50' },
          { label: 'Total Sales',        value: formatKSH(totalSales), color: 'text-purple-600',   bg: 'bg-purple-50', small: true },
        ].map((s, i) => (
          <div key={i} className="card">
            <div className={`card-body flex flex-col items-center justify-center text-center p-4 ${s.bg} rounded-lg`}>
              <span className={`text-2xl font-bold ${s.color} ${s.small ? 'text-lg break-all' : ''}`}>
                {s.value}
              </span>
              <span className="text-xs text-gray-600 mt-1">{s.label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          <button onClick={fetchSalespersons} className="underline text-sm ml-4">Retry</button>
        </div>
      )}

      {/* DataTable */}
      <div className="card">
        <div className="card-body">
          <DataTable
            data={salespersons}
            columns={columns}
            searchFields={searchFields}
            filename="salespersons"
            title="Salesperson Report"
            loading={loading}
            rowKey={row => row.id}
          />
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <SalespersonModal salesperson={editingSalesperson} onClose={handleClose} />
      )}
    </div>
  );
};

export default Salespersons;
